import React, { useState } from "react";
import AILightComponent from "../../../../components/AILightComponent";

const ScorePlusList = ({
  records,
  onEdit,
  onDelete,
  sampleMode,
  onClearFilters,
  confirmDelete,
  setConfirmDelete,
}) => {
  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);
  if (!records.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-4 py-10 text-center">
        <div className="mb-3 text-5xl">🔍</div>

        <h3 className="text-sm font-semibold text-yellow-300">
          No Matching Records
        </h3>

        <p className="mt-2 text-xs text-zinc-400">
          No activities match your selected filters.
        </p>

        <button
          onClick={onClearFilters}
          className="mt-4 rounded-md bg-yellow-500 w-fit px-3 py-1 text-xs font-medium text-black hover:bg-yellow-400"
        >
          ✨ Clear Filters
        </button>
      </div>
    );
  }

  return (
    <>
      {/* AI Help */}
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={toggleGenAIHelp}
          className="rounded-lg bg-purple-600 hover:bg-purple-500 w-fit px-3 py-1 text-xs font-semibold text-white"
        >
          {enableGenAIHelp ? "🙈 Hide AI Help" : "🤖 AI Help"}
        </button>
      </div>

      {enableGenAIHelp && (
        <div className="mb-4 w-full rounded-2xl border border-purple-500/20 bg-[#111827] p-2">
          <AILightComponent
            mode="scoreplus_list"
            param1="📈 ScorePlus Activity Intelligence Coach"
            param2={`
You are an Activity Performance & Productivity Coach.

The following ScorePlus records represent the user's completed and pending activities.

User Activity Records:

${records
  .map(
    (r, i) => `
${i + 1}.
• Date: ${new Date(r.date).toLocaleDateString()}
• Activity: ${r.act}
• Status: ${r.status}
• Duration: ${r.duration} ${r.durationUnit}
• Tags: ${(r.tags || []).join(", ") || "None"}
`,
  )
  .join("\n")}

These are the user's recent ScorePlus activities. Analyze the overall patterns instead of individual records.

Analyze the activity history and provide:

1. 📊 Overall Performance Summary
2. 💪 Strengths
3. ⚠️ Improvement Areas
4. 🔥 Consistency & Discipline Analysis
5. 🎯 Productivity Insights
6. 📈 Recommendations for better performance
7. 🚀 One motivating closing message

Keep the response under 250 words.
Use headings, emojis and bullet points.
Do NOT repeat raw JSON.
`}
          />
        </div>
      )}
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {records.map((row) => (
          <div
            key={row._id}
            className="bg-zinc-900 border border-zinc-700 rounded-xl p-4"
          >
            <div className="flex justify-between items-center mb-2">
              <div className="font-semibold text-white">{row.act}</div>

              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  row.status === "Done"
                    ? "bg-green-900 text-green-300"
                    : "bg-red-900 text-red-300"
                }`}
              >
                {row.status}
              </span>
            </div>

            <div className="text-sm text-gray-400">
              📅 {new Date(row.date).toLocaleDateString()}
            </div>

            <div className="text-sm text-gray-400 mt-1">
              ⏱ {row.duration} {row.durationUnit}
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {(row.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 rounded-full text-xs bg-cyan-900/40 text-cyan-300 border border-cyan-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* <div className="flex flex-row"> */}
            <div className="flex flex-row gap-2 mt-3">
              {confirmDelete?._id === row._id ? (
                <>
                  <button
                    type="button"
                    onClick={() => onDelete(row._id)}
                    className="w-fit px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs"
                  >
                    Confirm
                  </button>

                  <button
                    type="button"
                    onClick={() => setConfirmDelete(null)}
                    className="w-fit px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-white text-xs"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={sampleMode}
                    onClick={() => onEdit(row)}
                    className="w-fit px-2 py-1 rounded-lg bg-cyan-500 text-black font-semibold disabled:bg-zinc-700 disabled:text-gray-500"
                  >
                    ✏️
                  </button>

                  <button
                    type="button"
                    disabled={sampleMode}
                    onClick={() => setConfirmDelete(row)}
                    className="w-fit px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs"
                  >
                    🗑
                  </button>
                </>
              )}
            </div>
            {/* </div> */}
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-zinc-900 border border-zinc-700 rounded-xl overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-800">
            <tr>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Activity</th>
              <th className="p-2 text-left">Duration</th>
              <th className="p-2 text-left">Tags</th>
              <th className="p-2 text-center">Edit</th>
            </tr>
          </thead>

          <tbody>
            {records.map((row) => (
              <tr key={row._id} className="border-t border-zinc-800">
                <td className="p-2">
                  {new Date(row.date).toLocaleDateString()}
                </td>

                <td className="p-2">{row.status}</td>

                <td className="p-2">{row.act}</td>

                <td className="p-2">
                  {row.duration} {row.durationUnit}
                </td>

                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {(row.tags || []).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full text-xs bg-cyan-900/40 text-cyan-300 border border-cyan-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </td>

                <td className="p-2 text-center">
                  {confirmDelete?._id === row._id ? (
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => onDelete(row._id)}
                        className="w-fit px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-xs"
                      >
                        Confirm
                      </button>

                      <button
                        type="button"
                        onClick={() => setConfirmDelete(null)}
                        className="w-fit px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-white text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        disabled={sampleMode}
                        onClick={() => onEdit(row)}
                        className="w-fit p-1 text-cyan-400 disabled:text-gray-500"
                      >
                        ✏️
                      </button>

                      <button
                        type="button"
                        disabled={sampleMode}
                        onClick={() => setConfirmDelete(row)}
                        className="w-fit p-1 text-red-400 disabled:text-gray-500"
                      >
                        🗑
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ScorePlusList;
